// First-party advertising preference. No API imports: also used by the request interceptor.
export const MARKETING_EXCLUDED_KEY = "ytmethod_marketing_excluded";
export const CONSENT_VERSION = "meta-v1";
const KEY = "ytmethod_advertising_consent";
const CLICK_KEY = "ytmethod_meta_click";
const MAX_AGE = 180 * 86400000;
const BROWSER_KEY = "ytmethod_meta_browser";
let automaticTracking = false;

export function configureMarketing(config) {
  automaticTracking = config?.enabled === true && config?.consent_required === false;
}

export function marketingContext() {
  const explicit = advertisingConsent();
  if (explicit || !automaticTracking) return explicit;
  try {
    let browser = JSON.parse(localStorage.getItem(BROWSER_KEY));
    if (!browser?.id || browser.version !== CONSENT_VERSION || Date.now() - browser.at >= MAX_AGE) {
      browser = { id: window.crypto.randomUUID(), at: Date.now(), version: CONSENT_VERSION };
      localStorage.setItem(BROWSER_KEY, JSON.stringify(browser));
    }
    // Site configuration is not a visitor accepting a consent notice.
    return { ...browser, choice: "not_requested" };
  } catch { return null; }
}

export function marketingAllowed() {
  return ["granted", "not_requested"].includes(marketingContext()?.choice);
}

export function advertisingConsent() {
  try {
    const value = JSON.parse(localStorage.getItem(KEY));
    return value?.version === CONSENT_VERSION && ["granted", "denied"].includes(value.choice)
      && Date.now() - value.at < MAX_AGE ? value : null;
  } catch { return null; }
}

export function setAdvertisingConsent(choice) {
  if (!["granted", "denied"].includes(choice)) throw new Error("Invalid consent");
  const previous = advertisingConsent();
  const value = { id: previous?.id || window.crypto.randomUUID(), choice,
    at: Math.max(Date.now(), (previous?.at || 0) + 1), version: CONSENT_VERSION };
  localStorage.setItem(KEY, JSON.stringify(value));
  if (choice === "denied") localStorage.removeItem(CLICK_KEY);
  window.dispatchEvent(new Event("marketing-preference"));
  return value;
}

export function refreshAdvertisingDecision() {
  const value = advertisingConsent();
  if (value) {
    value.at = Math.max(Date.now(), value.at + 1);
    localStorage.setItem(KEY, JSON.stringify(value));
  }
  else {
    const browser = marketingContext();
    if (browser) localStorage.setItem(BROWSER_KEY, JSON.stringify({
      id: browser.id, at: Math.max(Date.now(), browser.at + 1), version: CONSENT_VERSION }));
  }
  window.dispatchEvent(new Event("marketing-preference"));
}

export function captureMetaClick(location = window.location) {
  if (!marketingAllowed() || localStorage.getItem(MARKETING_EXCLUDED_KEY) === "1") return;
  const click = new URLSearchParams(location.search).get("fbclid");
  if (!click || !/^[A-Za-z0-9_-]{1,500}$/.test(click)) return;
  let old;
  try { old = JSON.parse(localStorage.getItem(CLICK_KEY)); } catch { /* no saved click */ }
  if (old?.click === click && Date.now() - old.at < 90 * 86400000) return;
  const at = Date.now();
  localStorage.setItem(CLICK_KEY, JSON.stringify({ click, at, fbc: `fb.1.${at}.${click}` }));
}

export function marketingHeaders() {
  try {
    const excluded = localStorage.getItem(MARKETING_EXCLUDED_KEY) === "1";
    const consent = marketingContext();
    const headers = { "X-Marketing-Excluded": excluded ? "1" : "0" };
    if (!consent) return headers;
    Object.assign(headers, { "X-Marketing-Consent": consent.choice, "X-Marketing-Consent-Id": consent.id,
      "X-Marketing-Consent-At": String(consent.at), "X-Marketing-Consent-Version": consent.version });
    if (!excluded && ["granted", "not_requested"].includes(consent.choice)) {
      let click;
      try { click = JSON.parse(localStorage.getItem(CLICK_KEY)); } catch { /* no saved click */ }
      if (click && Date.now() - click.at < 90 * 86400000) headers["X-Marketing-Fbc"] = click.fbc;
      const cookie = document.cookie.split("; ").find((c) => c.startsWith("_fbp="));
      if (cookie) headers["X-Marketing-Fbp"] = cookie.slice(5);
    }
    return headers;
  } catch { return { "X-Marketing-Consent": "denied" }; }
}
